// src/components/OfferEdit/OfferEdit.js

import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '../Navbar/Navbar';
import api from '../Axios/axios';
import './OfferEdit.scss';

export default function OfferEdit() {
  const { id } = useParams();
  const navigate = useNavigate();

  // dropdown verileri
  const [owners, setOwners] = useState([]);
  const [kiracilar, setKiracilar] = useState([]);
  const [machinesAll, setMachinesAll] = useState([]);
  const [uniqueMachines, setUniqueMachines] = useState([]);

  // detay açıklamaları: metin ve id
  const [detailInputs, setDetailInputs] = useState([]);
  const [detailIds, setDetailIds] = useState([]);

  // form state
  const [kiralayan, setKiralayan] = useState('');
  const [ownerDetails, setOwnerDetails] = useState({ taxNo: '', address: '', contact: '', phone: '', email: '' });
  const [kiraci, setKiraci] = useState('');
  const [kiraciDetails, setKiraciDetails] = useState({ companyName: '', taxOffice: '', taxNo: '', address: '', contact: '', phone: '', email: '' });
  const [siteSelect, setSiteSelect] = useState('');
  const [siteInput, setSiteInput] = useState('');
  const [machine, setMachine] = useState('');
  const [machineDetails, setMachineDetails] = useState({ serial: '', brand: '', model: '', maxTonnage: '', height: '', jib: '', chassis: '', walking: false });
  const [rental, setRental] = useState({ start: '', months: 6, price: '', serviceItems: [] });
  const [plan, setPlan] = useState([]);
  const [rentalStatus, setRentalStatus] = useState('None');

  const statusOptions = [
    { value: 'None', label: 'Hiçbiri' },
    { value: 'Pending', label: 'Beklemede' },
    { value: 'Rented', label: 'Kiralandı' },
    { value: 'RentedElsewhere', label: 'Başka Yerde Kiralandı' },
    { value: 'ProjectCancelled', label: 'Proje İptal Edildi' }
  ];

  // load dropdowns & defaults
  useEffect(() => {
    api.get('/DefaultService').then(res => {
      // DÜZELTME 1: Varsayılan servisleri çekerken "Aylık Kira Bedeli"ni hariç tutuyoruz.
      const def = (res.data.data || [])
        .filter(s => s.name !== 'Aylık Kira Bedeli')
        .map(s => ({ label: s.name, price: s.price, qty: s.piece }));

      setRental(r => ({ ...r, serviceItems: def }));
    });

    api.get('/Company')
      .then(res => setOwners((res.data.data || []).filter(c => c.hasTowerCrane)));
    api.get('/Company', { params: { rentTowerCrane: true } })
      .then(res => setKiracilar(res.data.data.filter(c => c.rentTowerCrane)));
    api.get('/TowerCrane').then(res => {
      setMachinesAll(res.data.data);
      const seen = new Set();
      const uniq = res.data.data.filter(m => {
        const key = `${m.brand}-${m.model}-${m.maxTonnage}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      setUniqueMachines(uniq);
    });
    api.get('/Description', { params: { type: 'RentalOffer' } })
      .then(res => setDetailInputs((res.data.data || []).map(d => d.name || d.description)));
  }, []);

  // load existing offer
  useEffect(() => {
    if (!id) return;
    api.get(`/RentalOffer/${id}`).then(res => {
      const o = res.data.data || res.data;
      const startIso = o.startDate ? new Date(o.startDate).toISOString().slice(0, 10) : '';

      setKiralayan(o.rentingCompanyId || '');
      setKiraci(o.tenantCompany || '');
      setSiteSelect(o.buildingSite || '');
      setSiteInput('');
      setMachine(o.machine || '');

      setMachineDetails({
        serial: o.serialNo || '',
        brand: '',                  // payload’da brand yoksa boş
        model: o.model || '',
        maxTonnage: o.maxTonnage || '',
        height: o.installedHeight || '',
        jib: o.jibLength || '',
        chassis: o.type || '',
        walking: Boolean(o.hasWalkingGear)
      });

      setRental(r => ({
        ...r,
        start: startIso,
        months: o.rentalPeriod || 0,
        price: o.rent != null ? o.rent.toString() : '',

        // DÜZELTME 2: Kayıtlı teklifi yüklerken "Aylık Kira Bedeli" satırını siliyoruz.
        // Çünkü bu satır zaten yukarıdaki 'price' ve 'months' verisiyle otomatik oluşturuluyor.
        serviceItems: (o.services || [])
          .filter(s => s.name !== 'Aylık Kira Bedeli')
          .map(s => ({ label: s.name, price: s.price, qty: s.piece }))
      }));

      // descriptions: metin + id
      const descs = o.descriptions || [];
      setDetailIds(descs.map(d => d.id));
      setDetailInputs(descs.map(d => d.description || ''));

      setRentalStatus(o.rentalOfferStatus || 'None');

      setOwnerDetails({
        taxNo: o.rentingCompanyTaxOffice || '',
        address: o.rentingCompanyAddress || '',
        contact: o.rentingCompanyAuthorizedPerson || '',
        phone: o.rentingCompanyAuthorizedPersonPhone || '',
        email: o.rentingCompanyAuthorizedPersonEmail || ''
      });

      setKiraciDetails({
        companyName: o.tenantCompanyName || '',
        taxOffice: o.tenantCompanyTaxOffice || '',
        taxNo: o.tenantCompanyTaxNo || '',
        address: o.tenantCompanyAddress || '',
        contact: o.tenantCompanyAuthorizedPerson || '',
        phone: o.tenantCompanyAuthorizedPersonPhone || '',
        email: o.tenantCompanyAuthorizedPersonEmail || ''
      });

      setPlan((o.payments || []).map(p => ({
        no: p.no,
        date: (p.year && p.month)
          ? `${p.year}-${String(p.month).padStart(2, '0')}-01`
          : '',
        method: { Transfer: 'Havale', Cheque: 'Çek', Bond: 'Senet' }[p.paymentType] || 'Havale',
        amount: p.price
      })));
    });
  }, [id]);

  // kiralayan details
  useEffect(() => {
    if (!kiralayan) {
      setOwnerDetails({ taxNo: '', address: '', contact: '', phone: '', email: '' });
      return;
    }
    api.get(`/Company/GetById?id=${kiralayan}`).then(res => {
      const d = res.data.data || {};
      setOwnerDetails({
        taxNo: d.taxNo || '',
        address: d.address || '',
        contact: d.authorizedPerson1 || '',
        phone: d.phoneNumber1 || '',
        email: d.email || ''
      });
    });
  }, [kiralayan]);

  // kiracı details
  useEffect(() => {
    if (!kiraci) {
      setKiraciDetails({ companyName: '', taxOffice: '', taxNo: '', address: '', contact: '', phone: '', email: '' });
      return;
    }
    api.get(`/Company/GetById?id=${kiraci}`)
      .then(res => {
        const d = res.data.data || {};
        const parts = (d.taxNo || '').split('/');
        const office = parts[0] || '';
        const number = parts[1] || '';
        setKiraciDetails({
          companyName: d.name || '',
          taxOffice: office,
          taxNo: number,
          address: d.address || '',
          contact: d.authorizedPerson1 || '',
          phone: d.phoneNumber1 || '',
          email: d.email || ''
        });
      })
      .catch(err => console.error('Kiracı detayları alınırken hata:', err));
  }, [kiraci]);

  // machine details by label
  useEffect(() => {
    if (!machine) {
      setMachineDetails({ serial: '', brand: '', model: '', maxTonnage: '', height: '', jib: '', chassis: '', walking: false });
      return;
    }
    const sel = machinesAll.find(m => `${m.brand} ${m.model} ${m.maxTonnage} ton` === machine);
    if (sel) {
      setMachineDetails({
        serial: sel.serialNo || '',
        brand: sel.brand || '',
        model: sel.model || '',
        maxTonnage: sel.maxTonnage || '',
        height: sel.height || '',
        jib: sel.jibLength || '',
        chassis: sel.type || '',
        walking: Boolean(sel.hasWalkingGear)
      });
    }
  }, [machine, machinesAll]);

  // calculations
  const endDate = useMemo(() => {
    if (!rental.start) return '';
    const d = new Date(rental.start);
    d.setMonth(d.getMonth() + rental.months);
    d.setDate(d.getDate() - 1);
    return d.toISOString().slice(0, 10);
  }, [rental.start, rental.months]);

  const cleanRent = useMemo(() => parseFloat(rental.price.replace(/\./g, '').replace(/,/g, '.')) || 0, [rental.price]);

  const monthlyRentItem = useMemo(() => ({
    label: 'Aylık Kira Bedeli',
    price: cleanRent,
    qty: rental.months,
    total: cleanRent * rental.months * 1.2
  }), [cleanRent, rental.months]);

  const total = useMemo(() => rental.serviceItems.reduce((s, i) => s + i.price * i.qty, 0) + monthlyRentItem.total, [rental.serviceItems, monthlyRentItem]);
  const vatTotal = useMemo(() => total * 0.2, [total]);
  const totalWithVAT = useMemo(() => total + vatTotal, [total, vatTotal]);

  useEffect(() => {
    if (!rental.start) return;
    const arr = [];
    const kiraKdv = cleanRent * 1.2;
    const hizmetKdv = rental.serviceItems.reduce((s, i) => s + i.price * i.qty * 1.2, 0);
    for (let i = 0; i < rental.months; i++) {
      const dt = new Date(rental.start);
      dt.setMonth(dt.getMonth() + i);
      arr.push({
        no: i + 1,
        date: dt.toISOString().slice(0, 10),
        method: i === 0 ? 'Havale' : 'Çek',
        amount: i === 0 ? kiraKdv + hizmetKdv : kiraKdv
      });
    }
    setPlan(arr);
  }, [rental.start, rental.months, cleanRent, rental.serviceItems]);

  const onAmountChange = (idx, val) => {
    const num = Number(val) || 0;
    setPlan(prev => {
      const np = prev.map((x, i) => i === idx ? { ...x, amount: num } : x);
      const fixed = np.slice(0, idx + 1).reduce((s, x) => s + x.amount, 0);
      const restCnt = np.length - (idx + 1);
      const restAmt = restCnt > 0 ? (totalWithVAT - fixed) / restCnt : 0;
      for (let j = idx + 1; j < np.length; j++) np[j].amount = restAmt;
      return np;
    });
  };

  const mapPaymentType = m => ({ Havale: 'Transfer', Çek: 'Cheque', Senet: 'Bond' })[m] || 'Transfer';

  // save
  const saveOffer = () => {
    const payload = {
      id: Number(id),
      rentalOfferStatus: rentalStatus,
      rentingCompanyId: kiralayan,
      tenantCompany: kiraci,
      tenantCompanyName: kiraciDetails.companyName,
      tenantCompanyAddress: kiraciDetails.address,
      tenantCompanyAuthorizedPerson: kiraciDetails.contact,
      tenantCompanyAuthorizedPersonPhone: kiraciDetails.phone,
      tenantCompanyAuthorizedPersonEmail: kiraciDetails.email,
      buildingSite: siteInput || siteSelect,
      machine,
      model: machineDetails.model,
      serialNo: machineDetails.serial,
      type: machineDetails.chassis,
      installedHeight: Number(machineDetails.height) || 0,
      jibLength: Number(machineDetails.jib) || 0,
      hasWalkingGear: machineDetails.walking,
      startDate: rental.start,
      endDate,
      rentalPeriod: rental.months,
      rent: cleanRent,
      note: '',
      services: [
        { name: monthlyRentItem.label, price: monthlyRentItem.price, piece: monthlyRentItem.qty },
        ...rental.serviceItems.map(s => ({ name: s.label, price: Number(s.price), piece: s.qty }))
      ],
      total,
      vatTotal,
      totalWithVAT,
      payments: plan.map(p => ({
        no: p.no,
        paymentType: mapPaymentType(p.method),
        month: Number(p.date.slice(5, 7)),
        year: Number(p.date.slice(0, 4)),
        price: p.amount
      })),
      descriptions: detailInputs.map((desc, idx) => {
        const obj = { description: desc };
        const did = detailIds[idx];
        if (did != null) obj.id = did;
        return obj;
      })
    };

    api.put('/RentalOffer', payload)
      .then(() => { alert('Teklif güncellendi'); navigate('/offerlist'); })
      .catch(err => {
        console.error(err.response?.data || err);
        alert('Güncelleme hatası: ' + (err.response?.data?.message || err.message));
      });
  };

  return (
    <>
      <Navbar />
      <div className="offer-edit">
        <h2>Kira Teklifi Düzenle</h2>
        <div className="offer-form">
          {/* Form Üst Kısım */}
          <div className="form-row">
            <label>Kiralayan Firma</label>
            <select value={kiralayan} onChange={e => setKiralayan(e.target.value)}>
              <option value="">Seçin…</option>
              {owners.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </div>

          <div className="form-row multi">
            <div><label>Vergi No</label><input value={ownerDetails.taxNo} onChange={e => setOwnerDetails(d => ({ ...d, taxNo: e.target.value }))} /></div>
            <div><label>Adres</label><input value={ownerDetails.address} onChange={e => setOwnerDetails(d => ({ ...d, address: e.target.value }))} /></div>
            <div><label>Yetkili</label><input value={ownerDetails.contact} onChange={e => setOwnerDetails(d => ({ ...d, contact: e.target.value }))} /></div>
            <div><label>Tel</label><input value={ownerDetails.phone} onChange={e => setOwnerDetails(d => ({ ...d, phone: e.target.value }))} /></div>
            <div><label>Email</label><input value={ownerDetails.email} onChange={e => setOwnerDetails(d => ({ ...d, email: e.target.value }))} /></div>
          </div>

          <div className="form-row">
            <label>Kiracı Firma</label>
            <select value={kiraci} onChange={e => setKiraci(e.target.value)}>
              <option value="">Seçin…</option>
              {kiracilar.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>

          <div className="form-row multi">
            <div><label>Vergi Dairesi</label><input value={kiraciDetails.taxOffice} onChange={e => setKiraciDetails(d => ({ ...d, taxOffice: e.target.value }))} /></div>
            <div><label>Vergi No</label><input value={kiraciDetails.taxNo} onChange={e => setKiraciDetails(d => ({ ...d, taxNo: e.target.value }))} /></div>
            <div><label>Adres</label><input value={kiraciDetails.address} onChange={e => setKiraciDetails(d => ({ ...d, address: e.target.value }))} /></div>
            <div><label>Yetkili</label><input value={kiraciDetails.contact} onChange={e => setKiraciDetails(d => ({ ...d, contact: e.target.value }))} /></div>
            <div><label>Tel</label><input value={kiraciDetails.phone} onChange={e => setKiraciDetails(d => ({ ...d, phone: e.target.value }))} /></div>
            <div><label>Email</label><input value={kiraciDetails.email} onChange={e => setKiraciDetails(d => ({ ...d, email: e.target.value }))} /></div>
          </div>

          <div className="form-row multi">
            <div>
              <label>Şantiye (Manuel yazın veya seç)</label>
              <input
                type="text"
                placeholder="Manuel yazın"
                value={siteInput || siteSelect}
                onChange={e => setSiteInput(e.target.value)}
              />
            </div>
            <div>
              <label>Makine</label>
              <select value={machine} onChange={e => setMachine(e.target.value)}>
                <option value="" disabled>Seçin…</option>
                {uniqueMachines.map(m => {
                  const label = `${m.brand} ${m.model} ${m.maxTonnage} ton`;
                  return <option key={label} value={label}>{label}</option>;
                })}
              </select>
            </div>
            <div><label>Seri No</label><input value={machineDetails.serial} readOnly /></div>
            <div><label>Model</label><input value={machineDetails.model} readOnly /></div>
            <div><label>Yükseklik (m)</label><input type="number" value={machineDetails.height} readOnly /></div>
            <div><label>Bom (m)</label><input type="number" value={machineDetails.jib} readOnly /></div>
            <div>
              <label>Şase Tipi</label>
              <select value={machineDetails.chassis} disabled>
                <option value="">Seçin…</option>
                <option value="Chassis">Şaseli</option>
                <option value="Anchored">Statik</option>
              </select>
            </div>
            <div>
              <label>Yürüyüş Donanımı</label>
              <select value={String(machineDetails.walking)} disabled>
                <option value="true">Var</option>
                <option value="false">Yok</option>
              </select>
            </div>
          </div>

          <div className="form-row multi">
            <div><label>Başlangıç Tarihi</label><input type="date" value={rental.start} onChange={e => setRental(r => ({ ...r, start: e.target.value }))} /></div>
            <div><label>Süre (Ay)</label><input type="number" min="1" value={rental.months} onChange={e => setRental(r => ({ ...r, months: Number(e.target.value) }))} /></div>
            <div><label>Bitiş Tarihi</label><input type="date" value={endDate} readOnly /></div>
            <div><label>Aylık Kira (₺)</label><input value={rental.price} onChange={e => setRental(r => ({ ...r, price: e.target.value }))} /></div>
          </div>

          <div className="form-row">
            <label>Status</label>
            <select value={rentalStatus} onChange={e => setRentalStatus(e.target.value)}>
              {statusOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>

          {/* Servis & İşçilik Tablosu */}
          <h3>Servis & İşçilik</h3>
          <table className="service-table">
            <thead>
              <tr><th>Hizmet</th><th>Birim Fiyat</th><th>Adet / Ay</th><th>Tutar (KDV %20)</th><th>Sil</th></tr>
            </thead>
            <tbody>
              {/* Otomatik Hesaplanan Kira Satırı */}
              <tr>
                <td>{monthlyRentItem.label}</td>
                <td>{monthlyRentItem.price.toLocaleString()} ₺</td>
                <td>{monthlyRentItem.qty}</td>
                <td>{monthlyRentItem.total.toLocaleString()} ₺</td>
                <td>-</td>
              </tr>
              {/* Ekstra Hizmetler (Veritabanından gelen, filtreli) */}
              {rental.serviceItems.map((sv, idx) => {
                const rowTotal = sv.price * sv.qty * 1.2;
                return (
                  <tr key={idx}>
                    <td><input value={sv.label} onChange={e => { const v = e.target.value; setRental(r => { const a = [...r.serviceItems]; a[idx].label = v; return { ...r, serviceItems: a }; }); }} /></td>
                    <td><input type="number" value={sv.price} onChange={e => { const v = Number(e.target.value); setRental(r => { const a = [...r.serviceItems]; a[idx].price = v; return { ...r, serviceItems: a }; }); }} /></td>
                    <td><input type="number" min="1" value={sv.qty} onChange={e => { const v = Number(e.target.value); setRental(r => { const a = [...r.serviceItems]; a[idx].qty = v; return { ...r, serviceItems: a }; }); }} /></td>
                    <td>{rowTotal.toLocaleString()} ₺</td>
                    <td><button className="remove-btn" onClick={() => setRental(r => ({ ...r, serviceItems: r.serviceItems.filter((_, i) => i !== idx) }))}>—</button></td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr><td colSpan="4">Toplam</td><td>{totalWithVAT.toLocaleString()} ₺</td></tr>
            </tfoot>
          </table>
          <button className="btn-small" onClick={() => setRental(r => ({ ...r, serviceItems: [...r.serviceItems, { label: '', price: 0, qty: 1 }] }))}>+ Hizmet Ekle</button>

          <div className="totals">
            <div>KDV Hariç Toplam: {total.toLocaleString()} ₺</div>
            <div>KDV (%20): {vatTotal.toLocaleString()} ₺</div>
            <div>KDV Dahil Toplam: {totalWithVAT.toLocaleString()} ₺</div>
          </div>

          {/* Ödeme Planı */}
          <h3>Ödeme Planı</h3>
          <div className="plan-wrapper">
            <table className="plan">
              <thead><tr><th>No</th><th>Ay</th><th>Şekil</th><th>Tutar</th><th>İşlem</th></tr></thead>
              <tbody>
                {plan.map((p, i) => (
                  <tr key={i}>
                    <td>{p.no}</td>
                    <td><input value={p.date} onChange={e => { const d = e.target.value; setPlan(prev => prev.map((x, j) => j === i ? { ...x, date: d } : x)); }} /></td>
                    <td><select value={p.method} onChange={e => { const m = e.target.value; setPlan(prev => prev.map((x, j) => j === i ? { ...x, method: m } : x)); }}>
                      <option>Havale</option><option>Çek</option><option>Senet</option>
                    </select></td>
                    <td><input type="number" value={p.amount} onChange={e => onAmountChange(i, e.target.value)} /></td>
                    <td><button className="remove-btn" onClick={() => setPlan(prev => prev.filter((_, j) => j !== i))}>—</button></td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan="4" className="add-plan" onClick={() => setPlan(prev => [...prev, { no: prev.length + 1, date: '', method: 'Çek', amount: 0 }])}>+ Ödeme Ekle</td>
                  <td>{plan.reduce((s, p) => s + p.amount, 0).toLocaleString()} ₺</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Detaylar */}
          <h3>Detay Açıklamaları</h3>
          {detailInputs.map((desc, idx) => (
            <div className="form-row details-container" key={idx}>
              <input
                className="detail-input"
                value={desc}
                onChange={e => {
                  const all = [...detailInputs];
                  all[idx] = e.target.value;
                  setDetailInputs(all);
                }}
              />
            </div>
          ))}
          <button
            className="btn-small"
            onClick={() => {
              setDetailInputs([...detailInputs, '']);
              setDetailIds([...detailIds, null]);
            }}
          >
            + Detay Ekle
          </button>

          <button className="btn-primary" onClick={saveOffer}>
            Güncelle
          </button>
        </div>
      </div>
    </>
  );
}
